import * as React from "react";
// Lodash
import map from "lodash/map";
import throttle from "lodash/throttle";
// Controls
import { DropTarget } from "react-dnd";
import { BaseComponent } from "../../BaseComponent";
// Styles
import { getDndListClassNames } from "./DndList.styles";
/** Task for converting DndList class component to Function component: https://office.visualstudio.com/PathWM/_workitems/edit/8394648 */ /**
 * Standalone DnD list component
 */ class DndList extends BaseComponent {
    /**
     * Name of the component
     * @override
     */ name() {
        return "DndList";
    }
    /**
     * When dragging a list item, check if it is in bounds. If not, don't show placeholder.
     * @param nextProps The next version of props
     * @override
     */ componentDidUpdate(prevProps) {
        if (this.props.isOver !== prevProps.isOver && this.props.isOver === false) {
            this.setState({
                draggedEntity: null,
                placeholderIndex: null
            });
        }
    }
    /**
     * Render the list
     * @override
     */ renderComponent() {
        const { listItems, isReorderDisabled, onRenderItem, ariaLabel, itemType, connectDropTarget } = this.props;
        const updatedList = [
            ...listItems
        ];
        const classNames = this.getClassNames();
        // If drag-n-drop in progress, remove the dragged item.
        if (this.state.placeholderIndex != null && this.state.draggedEntityIndex != null) {
            updatedList.splice(this.state.draggedEntityIndex, 1);
        }
        const dataJsx = map(updatedList, (listItem, index)=>onRenderItem(listItem, index, itemType, updatedList, isReorderDisabled));
        if (this.state.placeholderIndex != null) {
            dataJsx.splice(this.state.placeholderIndex, 0, /*#__PURE__*/ React.createElement("li", {
                className: "listItem placeholder",
                key: DndList.placeholderRole,
                "data-dnd-role": DndList.placeholderRole
            }, /*#__PURE__*/ React.createElement("div", null)));
        }
        const componentJsx = /*#__PURE__*/ React.createElement("ul", {
            className: classNames.root,
            "aria-label": ariaLabel
        }, dataJsx);
        return isReorderDisabled || !connectDropTarget ? componentJsx : connectDropTarget(componentJsx);
    }
    /**
     * Specifies the drop target methods for this component
     */ itemTarget() {
        return {
            drop: this.onDrop.bind(this),
            hover: throttle(this.hover.bind(this), 50)
        };
    }
    /**
     * Specifies which props for React DnD to inject into this component for dropping
     */ itemTargetCollect(connect, monitor) {
        return {
            connectDropTarget: connect.dropTarget(),
            isOver: monitor.isOver()
        };
    }
    /**
     * Handle item being dropped onto this list
     */ onDrop(props, monitor, component) {
        if (!monitor || monitor.getItemType() !== props.itemType) {
            return;
        }
        const draggedEntityIndex = monitor.getItem().index;
        if (!component || draggedEntityIndex == null || draggedEntityIndex < 0) {
            return;
        }
        const listItem = props.listItems[draggedEntityIndex];
        if (!listItem) {
            return;
        }
        const placeholderIndex = component.state.placeholderIndex;
        if (monitor.getItemType() === props.itemType && placeholderIndex != null && placeholderIndex !== draggedEntityIndex) {
            props.onReorder(listItem, placeholderIndex - draggedEntityIndex);
        }
        // Reset state variables to hide placeholder
        component.setState({
            draggedEntity: null,
            draggedEntityIndex: null,
            placeholderIndex: null
        });
    }
    /**
     * Handle dragged item hovering over list by moving the placeholder as needed.
     * @param props The list properties
     * @param [monitor] The drop target monitor object
     * @param [component] The list component
     */ hover(props, monitor, component) {
        if (!monitor || monitor.getItemType() !== props.itemType || !component) {
            return;
        }
        const list = component;
        const clientOffset = monitor.getClientOffset();
        if (clientOffset == null) {
            return;
        }
        // Get the delta between the current and previous pointer y position, setting to 0 if there has not yet been a move
        const pointerYDelta = list._previousPointerYPosition == null ? 0 : clientOffset.y - list._previousPointerYPosition;
        // Update the previous pointer y position
        list._previousPointerYPosition = clientOffset.y;
        // The element at the location of the pointer
        let element = document.elementFromPoint(clientOffset.x, clientOffset.y);
        // Traverse the parent chain of the element until we find an element with a 'data-dnd-role' attribute
        element = list.findTarget(element);
        if (element && element.getAttribute(DndList.dataDndRoleAttribute) === props.itemType) {
            // Check if pointer is above the midpoint of a column, in order to render the placeholder in the correct position
            const isAboveCard = clientOffset.y - (element.getBoundingClientRect().top + element.getBoundingClientRect().bottom) / 2 <= 0;
            const dataIdx = element.getAttribute(DndList.dataIndexAttribute);
            if (!dataIdx) {
                return;
            }
            let index = parseInt(dataIdx, 10);
            // If there is not a placeholder adjacent to the card being hovered over and we are dragging below the midpoint of a card, increase the index by 1
            if ((list.state.placeholderIndex == null || Math.abs(index - list.state.placeholderIndex) > 1) && !isAboveCard) {
                // Adjust the index
                index += 1;
            }
            // If the card we are dragging over is adjacent to the placeholder on its far side, increase index by 1
            if (list.state.placeholderIndex === index) {
                index += 1;
            }
            // To prevent flickering when dragging small cards over big cards, this prevents changing the placeholder if we are dragging through a card towards a placeholder
            if (list.state.placeholderIndex != null && // If the pointer is moving in the negative direction on the y axis and the placeholder is below the card we are hovering over
            (pointerYDelta < 0 && index === list.state.placeholderIndex + 1 || // If the pointer is moving in the positive direction on the y axis and the placeholder is above the card we are hovering over
            pointerYDelta > 0 && index === list.state.placeholderIndex - 1 || // If we are rendering a placeholder and the pointer delta is 0
            pointerYDelta === 0)) {
                return;
            }
            list.setPlaceholder(index, monitor.getItem());
        }
    }
    /**
     * Traverses the parent chain of an element until it finds an element with an attribute specifying its drag and drop role
     */ findTarget(element) {
        if (element == null) {
            return null;
        }
        const role = element.getAttribute(DndList.dataDndRoleAttribute);
        if (role === DndList.placeholderRole) {
            return null;
        }
        if (role === this.props.itemType) {
            return element;
        }
        if (role == null) {
            element = element.parentElement;
            element = this.findTarget(element);
        }
        return element;
    }
    /**
     * Modifies the array of entities on the state to include a placeholder card
     * @param index Index of the card being hovered over
     * @param item The item being dragged
     */ setPlaceholder(index, item) {
        this.setState({
            draggedEntity: item.entity,
            draggedEntityIndex: item.index,
            placeholderIndex: index
        });
    }
    /**
     * Get the classnames associated with the computed styles
     */ getClassNames() {
        return getDndListClassNames(this.props.styles);
    }
    /**
     * Initializes a new instance of list
     * @param props Props for the list
     * @param context Context to pass onto base component
     */ constructor(props, context){
        super(props, context);
        this.state = {
            draggedEntity: null,
            draggedEntityIndex: null,
            placeholderIndex: null
        };
        this._previousPointerYPosition = null;
    }
}
DndList.placeholderRole = "placeholder";
DndList.dataDndRoleAttribute = "data-dnd-role";
DndList.dataIndexAttribute = "data-index";
export default DropTarget((props)=>props.itemType, DndList.prototype.itemTarget(), DndList.prototype.itemTargetCollect)(DndList);
